import { createSelector, Dispatch } from '@reduxjs/toolkit';
import { AvailabilitySchedule } from 'gql/autogen/AvailabilitySchedule';
import { selectIsCalendarLoading, selectIsSchedulesAllUnavailable, selectIsSchedulesEmpty, selectSchedulesFromCalendarState } from 'component/page/detail/store/calendar.selectors';
import { CmsValue } from 'core/type/BootstrapConfig.types';
import { selectCmsFromBootstrap, selectFeatureBehaviorFromBootstrap } from 'store/slice/bootstrap';
import { selectAvailabilitySelectedDate, selectCheapestAvailableOffer } from 'component/page/detail/store/availabilityDEPRECATED.selectors';
import { fetchAvailability, resetSelectedOfferCard, setSelectedDate } from 'component/page/detail/store/availabilityIndex';
import { setTravelDateInSearchBoxSettingsState } from 'store/slice/home';
import { getDateInYmdFormat } from 'core-framework/util/datetime/date';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesDateChipButtonClickEventActivitiesFieldsV1 } from '@agoda/analytics-messages-definition-activities/events.split';
import { ActivitiesDateChipButtonClickEventActivitiesManifestV1 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { selectActivityRepresentativeInfo, selectActivityTokenFromDetailState } from 'component/page/detail/store/detail.selectors';
import { ScreenSize, selectScreenSize } from 'store/slice/screen';
import { getOfferWithMinPricingSummary, mapTotalPriceByOffer } from 'gql/service/graph/activityService';
import { mapPriceNumbers } from 'component/page/detail/PopoverCalendar/PopoverCalendarUtils';
import { RepresentativeInfo } from 'gql/autogen/RepresentativeInfo';
import { Offer } from 'gql/autogen/Offer';

interface StateProps {
    cms: CmsValue;
    isScheduleReady: boolean;
    isSchedulesAllUnavailable: boolean;
    availabilitySchedule: AvailabilitySchedule[];
    selectedDate: Date;
    activityTokenFromState: string;
    screenSize: ScreenSize;
    activityLevelOfferPrice: number;
    activityLevelCalendarPrice: number;
    isDetailPageForSimCardEnabled: boolean;
}

interface DispatchProps {
    setSelectedDate: (activityToken: string, selectedDate: Date) => void;
    resetSelectedOfferCard: () => void;
}

export type Props = StateProps & DispatchProps;
export const mapStateToProps = createSelector(
    selectCmsFromBootstrap,
    selectIsSchedulesEmpty,
    selectIsCalendarLoading,
    selectSchedulesFromCalendarState,
    selectIsSchedulesAllUnavailable,
    selectAvailabilitySelectedDate,
    selectActivityTokenFromDetailState,
    selectScreenSize,
    selectFeatureBehaviorFromBootstrap,
    selectActivityRepresentativeInfo,
    selectCheapestAvailableOffer,
    (
        cms,
        isSchedulesEmpty,
        isLoading,
        availabilitySchedule,
        isSchedulesAllUnavailable,
        selectedDate,
        activityTokenFromState,
        screenSize,
        featureBehaviors,
        representativeInfo?: RepresentativeInfo,
        cheapestOffer?: Offer,
    ): StateProps => {
        const representativePricingSummary = representativeInfo?.pricingSummary;
        const cheapestPricingSummary = getOfferWithMinPricingSummary(cheapestOffer?.enrichedOfferOption || [])?.pricingSummary;
        const pricingSummary = cheapestPricingSummary || representativePricingSummary;
        const activityLevelOfferPrice = mapTotalPriceByOffer(pricingSummary);
        const activityLevelPriceSchedules = mapPriceNumbers(availabilitySchedule);
        const activityLevelCalendarPrice =
            activityLevelPriceSchedules.length > 0
                ? activityLevelPriceSchedules.find((label) => getDateInYmdFormat(label.date) === getDateInYmdFormat(selectedDate))?.price || activityLevelPriceSchedules[0]?.price
                : 0;

        const isDetailPageForSimCardEnabled = featureBehaviors.DetailPageForSimCard;

        return {
            cms,
            isSchedulesAllUnavailable,
            availabilitySchedule,
            selectedDate,
            activityTokenFromState,
            isScheduleReady: !isSchedulesEmpty && !isLoading,
            screenSize,
            activityLevelOfferPrice,
            activityLevelCalendarPrice,
            isDetailPageForSimCardEnabled,
        };
    },
);

export const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
    setSelectedDate: (activityToken: string, date: Date) => {
        const travelDate = getDateInYmdFormat(date);
        trackElementEvent<ActivitiesDateChipButtonClickEventActivitiesFieldsV1>(ActivitiesDateChipButtonClickEventActivitiesManifestV1.ids, {}, { travel_date: travelDate });
        dispatch(setSelectedDate({ selectedDate: date }));
        dispatch(setTravelDateInSearchBoxSettingsState(date));
        fetchAvailability(activityToken, { excludePassengers: false });
    },
    resetSelectedOfferCard: () => {
        dispatch(resetSelectedOfferCard());
    },
});
